import { CanvaSX, isTestEnv } from '../canvasx';
import { defaultShapeProps } from '../shapes';
import { drawRect } from '../util';

const imageCache = new Map<string, HTMLImageElement>();

const defaultImageProps = { ...defaultShapeProps };

export type ImageProps = Partial<Omit<ShapeStruct, 'borderRadius'>> &
  DimensionStruct & {
    // For now, images require fixed dimensions, as it would complicate the layout/dimension calculation
    width: number;
    height: number;

    // Other image props
    crossOrigin?: boolean;
    rounded?: boolean;
    src: string;
  };

export const Image: CanvaSXComponent<
  ImageProps,
  Omit<typeof defaultImageProps, 'borderRadius'>
> = function (props) {
  const { crossOrigin = false, src, width, height, rounded = false } = props;
  const { canvas, ctx, object } = CanvaSX.context;

  let image = imageCache.get(src);
  if (!image) {
    image = new window.Image();

    image.addEventListener('error', () => {
      image?.setAttribute('hasError', '1');
    });

    // TODO: default dev env loads avatars through S3, and does not have CORS headers currently
    if (window.railsEnv !== 'development' && crossOrigin) {
      image.crossOrigin = 'anonymous';
    }

    image.addEventListener('load', async () => {
      await image?.decode();
      object.controller?.renderManager?.refreshObject(
        object as unknown as fabric.Object
      );
      canvas.requestRenderAll();
    });

    image.src = src;
    imageCache.set(src, image);
  }

  drawRect(ctx, { ...props, width, height }, () => {
    const renderImage =
      isTestEnv || (image?.complete && !image.getAttribute('hasError'));

    if (renderImage) {
      ctx.save();

      if (rounded) {
        ctx.beginPath();
        ctx.arc(width / 2, height / 2, width / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
      }

      // Catch and suppress errors
      try {
        // If the image is not cross-origin, and it's from a different origin, we can't render it while exporting
        const skipRender =
          !crossOrigin &&
          canvas.exporting &&
          !src.startsWith(location.origin) &&
          !src.startsWith('data:');

        if (!skipRender) {
          if (isTestEnv) {
            ctx.save();
            ctx.fillStyle = 'red';
            ctx.fillRect(0, 0, width, height);
            ctx.restore();
          } else if (image) {
            ctx.drawImage(image, 0, 0, width, height);
          } else {
            console.error('Not rendering image.');
          }
        }
      } catch (ex) {
        console.error('Error rendering image to canvas:', ex);
      }

      ctx.restore();
    }
  });

  return null;
};

Image.defaultProps = defaultImageProps;
